import { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, getStatusBadge, ConfirmModal } from '@/backoffice/components/ui';
import { OrderChatPanel } from '@/backoffice/components/messaging/OrderChatPanel';
import { formatCurrency, formatRelativeTime } from '@/backoffice/utils';
import {
  fetchClientOrders,
  fetchOrderDetail,
  cancelOrder,
  type Order,
} from '@/domains/client/services/client.service';
import { isCashMethod, isMobileMoneyMethod } from '@/domains/client/utils/paymentMethod';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { showSuccess } from '@/utils/handleErrorApi';
import {
  Plus,
  Package,
  MapPin,
  ArrowRight,
  X,
  Loader2,
  Smartphone,
  Banknote,
} from 'lucide-react';

type OrderStatus = 'pending' | 'in_progress' | 'delivered' | 'confirmed_livree' | 'cancelled' | 'rejected';

export function OrdersList() {
  const location = useLocation();
  const isHistory = location.pathname.includes('/historique');

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<OrderStatus | 'all' | 'history'>(
    isHistory ? 'history' : 'all',
  );

  useEffect(() => {
    fetchClientOrders()
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  const statusFilters: { value: OrderStatus | 'all' | 'history'; label: string; count: number }[] = [
    { value: 'all', label: 'Toutes', count: orders.length },
    { value: 'pending', label: 'En attente', count: orders.filter((o) => o.statut === 'pending').length },
    { value: 'in_progress', label: 'En cours', count: orders.filter((o) => o.statut === 'in_progress').length },
    { value: 'delivered', label: 'Livrées', count: orders.filter((o) => o.statut === 'delivered').length },
    { value: 'history', label: 'Terminées', count: orders.filter((o) => ['delivered', 'confirmed_livree'].includes(o.statut)).length },
    { value: 'cancelled', label: 'Annulées', count: orders.filter((o) => o.statut === 'cancelled').length },
  ];

  const filteredOrders = activeFilter === 'all'
    ? orders
    : activeFilter === 'history'
      ? orders.filter((o) => ['delivered', 'confirmed_livree'].includes(o.statut))
      : orders.filter((o) => o.statut === activeFilter);

  return (
    <DashboardLayout userType="client" title={isHistory ? 'Historique' : 'Mes commandes'}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto">
          {statusFilters.map((filter) => (
            <Button
              key={filter.value}
              variant={activeFilter === filter.value ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(filter.value)}
              className="whitespace-nowrap"
            >
              {filter.label}
              <Badge
                variant={activeFilter === filter.value ? 'primary' : 'default'}
                size="sm"
                className="ml-2"
              >
                {filter.count}
              </Badge>
            </Button>
          ))}
        </div>
        <Link to="/dashboard/client/commandes/nouvelle">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            Nouvelle commande
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement des commandes...
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune commande</h3>
            <p className="text-gray-500 mb-6">Vous n'avez pas encore de commande dans cette catégorie.</p>
            <Link to="/dashboard/client/commandes/nouvelle">
              <Button>Créer une commande</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          {filteredOrders.map((order) => (
            <OrderThumb key={order.id} order={order} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

const STATUS_ACCENT: Record<string, string> = {
  delivered: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  confirmed_livree: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  in_progress: 'bg-blue-50 text-blue-600 border-blue-100',
  pending: 'bg-amber-50 text-amber-600 border-amber-100',
  cancelled: 'bg-rose-50 text-rose-600 border-rose-100',
  rejected: 'bg-rose-50 text-rose-600 border-rose-100',
};

function shortPlace(value: string): string {
  if (!value) return '—';
  const part = value.split('—')[0]?.trim() || value;
  return part.length > 28 ? `${part.slice(0, 28)}…` : part;
}

/** Miniature compacte — lisible même avec beaucoup de commandes */
function OrderThumb({ order }: { order: Order }) {
  const driver = order.livraisons?.[0]?.livreur;
  const accent = STATUS_ACCENT[order.statut] ?? 'bg-slate-50 text-slate-600 border-slate-100';

  return (
    <Link
      to={`/dashboard/client/commandes/${order.id}`}
      className="group block rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm hover:border-teal-300 hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${accent}`}>
          <Package className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-sm text-slate-900 truncate group-hover:text-teal-700">
                {order.code_suivi}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">{formatRelativeTime(order.created_at)}</p>
            </div>
            <div className="shrink-0 scale-90 origin-top-right">{getStatusBadge(order.statut)}</div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-600">
        <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="truncate">{shortPlace(order.adresse_ramassage)}</span>
        <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
        <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
        <span className="truncate">{shortPlace(order.adresse_livraison)}</span>
      </div>

      <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {order.type_livraison && (
            <span className="text-[11px] font-medium text-slate-500 truncate">{order.type_livraison}</span>
          )}
          {driver && (
            <span className="text-[11px] text-slate-400 truncate hidden sm:inline">
              · {driver.firstnames}
            </span>
          )}
          {order.payment_status === 'paid' && (
            <Badge variant="success" size="sm" className="!text-[10px] !px-1.5 !py-0">Payée</Badge>
          )}
          {order.payment_status === 'refunded' && (
            <Badge variant="warning" size="sm" className="!text-[10px] !px-1.5 !py-0">Remboursée</Badge>
          )}
          {order.statut === 'delivered' && order.payment_status !== 'paid' && (
            <Badge variant="warning" size="sm" className="!text-[10px] !px-1.5 !py-0">À payer</Badge>
          )}
        </div>
        {order.prix_estime != null && (
          <p className="text-sm font-bold text-slate-900 tabular-nums shrink-0">
            {formatCurrency(order.prix_estime)}
          </p>
        )}
      </div>
    </Link>
  );
}

export function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = Number(id);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    if (!orderId) return;
    fetchOrderDetail(orderId)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    setCancelError('');
    try {
      const updated = await cancelOrder(order.id);
      setOrder(updated);
      setShowCancelModal(false);
      showSuccess('Commande annulée.');
      navigate('/dashboard/client/commandes');
    } catch (e) {
      setCancelError(getApiErrorMessage(e, 'Impossible d\'annuler la commande.'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="client" title="Commande">
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout userType="client" title="Commande introuvable">
        <Card className="text-center py-12">
          <p className="text-gray-500">Cette commande n'existe pas ou n'est plus accessible.</p>
          <Link to="/dashboard/client/commandes" className="mt-4 inline-block">
            <Button>Retour aux commandes</Button>
          </Link>
        </Card>
      </DashboardLayout>
    );
  }

  const driver = order.livraisons?.[0]?.livreur;
  const cashOrder = isCashMethod(order.payment_method);
  const mobileOrder = isMobileMoneyMethod(order.payment_method);

  return (
    <DashboardLayout
      userType="client"
      title={order.code_suivi}
      breadcrumbs={[
        { label: 'Commandes', path: '/dashboard/client/commandes' },
        { label: order.code_suivi },
      ]}
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Statut de la commande</h3>
            <div className="flex items-center gap-3">
              {getStatusBadge(order.statut)}
              <span className="text-sm text-gray-500">
                Créée {formatRelativeTime(order.created_at)}
              </span>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Adresses</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Collecte</span>
                </div>
                <p className="font-medium text-gray-900">{order.adresse_ramassage}</p>
              </div>
              <div className="border-l-2 border-dashed border-gray-200 h-4 ml-2" />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-gray-500">Livraison</span>
                </div>
                <p className="font-medium text-gray-900">{order.adresse_livraison}</p>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Messages</h3>
            <OrderChatPanel orderId={order.id} />
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Paiement</h3>
            {order.payment_status === 'refunded' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut</span>
                  <Badge variant="warning">Remboursé</Badge>
                </div>
                <p className="text-gray-500">
                  Le montant a été recrédité sur votre portefeuille après annulation.
                </p>
              </div>
            ) : order.payment_status === 'paid' || order.statut === 'confirmed_livree' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut</span>
                  <Badge variant="success">Payé</Badge>
                </div>
                {order.payment_method && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mode</span>
                    <span className="font-medium">{order.payment_method}</span>
                  </div>
                )}
                {order.payment_reference && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Référence</span>
                    <span className="font-mono text-xs">{order.payment_reference}</span>
                  </div>
                )}
                {order.payment_timing && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Moment</span>
                    <span className="font-medium">
                      {order.payment_timing === 'before_delivery' ? 'Avant livraison' : 'Après livraison'}
                    </span>
                  </div>
                )}
              </div>
            ) : order.statut === 'delivered' ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Confirmez la réception et réglez le livreur (Mobile Money ou espèces).
                </p>
                <Link to={`/dashboard/client/commandes/${order.id}/paiement`}>
                  <Button fullWidth leftIcon={<Smartphone className="w-4 h-4" />}>
                    Confirmer & Payer
                  </Button>
                </Link>
              </div>
            ) : cashOrder ? (
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-start gap-2">
                  <Banknote className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    Paiement en espèces à la livraison
                    {order.payment_method ? <> — <span className="font-medium">{order.payment_method}</span></> : null}.
                    Aucun numéro Mobile Money requis.
                  </span>
                </p>
              </div>
            ) : order.payment_method ? (
              <div className="space-y-2 text-sm text-gray-600">
                <p>Mode choisi : <span className="font-medium">{order.payment_method}</span></p>
                {order.payment_status === 'pending' && mobileOrder && (
                  <Link to={`/dashboard/client/commandes/${order.id}/paiement`}>
                    <Button size="sm" variant="outline" leftIcon={<Smartphone className="w-4 h-4" />}>
                      Payer maintenant
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Paiement à la livraison — disponible une fois le colis livré.</p>
            )}
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Récapitulatif</h3>
            <div className="space-y-3">
              {order.type_livraison && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Type</span>
                  <span className="font-medium">{order.type_livraison}</span>
                </div>
              )}
              {order.prix_estime != null && (
                <div className="flex justify-between pt-3 border-t border-gray-100">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-teal-600">{formatCurrency(order.prix_estime)}</span>
                </div>
              )}
            </div>
            {order.statut === 'pending' && (
              <Button
                variant="danger"
                fullWidth
                className="mt-4"
                leftIcon={<X className="w-4 h-4" />}
                onClick={() => { setCancelError(''); setShowCancelModal(true); }}
              >
                Annuler la commande
              </Button>
            )}
          </Card>

          {driver && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Livreur</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">
                    {driver.firstnames[0]}{driver.lastname[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{driver.firstnames} {driver.lastname}</p>
                  <p className="text-sm text-gray-500">{driver.phone}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => !cancelling && setShowCancelModal(false)}
        onConfirm={handleCancel}
        title="Annuler la commande ?"
        message={cancelError || 'Cette action est définitive. La commande ne sera plus proposée aux livreurs.'}
        confirmText="Oui, annuler"
        cancelText="Garder"
        variant="danger"
        isLoading={cancelling}
      />
    </DashboardLayout>
  );
}
